// Where a question is being asked FROM (busy-main-bff#20).
//
// The BFF bounds retrieval to a scope — "home", "ongoing:<id>", "pack:<id>", "thread:<id>",
// "object:<type>:<id>" — and falls back to the whole mailbox, visibly, when the scope has too little
// to answer from. This module is the client half of that contract: it turns "where the user is" into
// the string, and the answer's scope block into the one line we show them.
//
// Pure on purpose (no store, no router): the rule is "which surface am I on", and it should be
// readable — and testable — without mounting anything.

/** The wire form for a place. Anything unnamed is home, which is what every surface asked before. */
export function scopeString ({ ongoingId, packId, threadId, objectType, objectId } = {}) {
  // Most specific first: a pack is open INSIDE an ongoing, and a thread inside either.
  if (objectType && objectId) return `object:${String(objectType).toLowerCase()}:${objectId}`
  if (threadId) return `thread:${threadId}`
  if (packId) return `pack:${packId}`
  if (ongoingId) return `ongoing:${ongoingId}`
  return 'home'
}

/**
 * The scope of a vue-router route. Reads only what the app's own routes carry:
 *   /ongoing/:id           → that story
 *   /ongoing/:id?pack=…    → that pack (the pack view is a query on the story route)
 *   /emails/timeline/:id   → that thread (the single-thread page)
 *   ?open=<timelineId>     → the thread the user has open (email lists open a thread this way)
 */
export function scopeFromRoute (route) {
  if (!route) return 'home'
  const params = route.params || {}
  const query = route.query || {}
  const path = String(route.path || '')

  const packId = query.pack || undefined
  // A thread is open either as its own page or as a query on a list; both are the same question.
  const threadId = query.open || (path.startsWith('/emails/timeline/') ? params.id : undefined) || undefined
  const ongoingId = path.startsWith('/ongoing/') ? (params.id || params.ongoingId) : undefined

  return scopeString({ ongoingId, packId, threadId })
}

/**
 * The sentence to show under an answer that had to widen — and nothing at all otherwise. The BFF
 * writes the reason; we only fall back to a wording of our own if it sent none, so the two never
 * drift into saying different things.
 */
export function fallbackLine (scope) {
  if (!scope || !scope.fallback) return ''
  return scope.reason || 'Nothing in this Ongoing — searched everything'
}

/**
 * The answer's scope block, as a turn should hold it — or null when the answer said nothing about
 * its scope at all (busy-my-web#367).
 *
 * Every transport hands this back differently: the one-shot `ai/ask` endpoint puts it on the
 * response body, the SSE stream puts it on the `done` event, and an older BFF sends neither. The
 * in-story chat read NONE of them, so a search the server had widened past the Ongoing answered as
 * though it had stayed inside it — the notice `fallbackLine` exists to draw was simply never given
 * anything to draw from. Normalising here means both transports produce one shape, and the line the
 * user reads cannot depend on which one answered.
 *
 * Null for "nothing to say": no block, a non-object, or a block with no effective scope, no
 * fallback and no reason. A turn holding null draws no line, which is what an ordinary scoped
 * answer should do.
 */
export function answerScope (scope) {
  if (!scope || typeof scope !== 'object' || Array.isArray(scope)) return null
  const effective = scope.effective ? String(scope.effective) : ''
  const fallback = !!scope.fallback
  const reason = scope.reason ? String(scope.reason) : ''
  if (!effective && !fallback && !reason) return null
  return { effective: effective || 'home', fallback, ...(reason ? { reason } : {}) }
}
